import { useState, useMemo, useRef, useEffect } from "react";
import { shuffle, random, words } from "lodash";

import { useRecord, useRecords } from "@latticexyz/stash/react";
import mudConfig from "contracts/mud.config";
import { stash } from "../../../mud/stash";
import { useSessionPlayer } from "../../../SessionPlayerContext";

import { secretCfg, writeCfg } from "../../../config.js";

import { useGuide } from "../../GuideContext";
import MainButton from "../../../components/MainButton";

import ExtraAudio from "../../../ExtraAudio.js";

import { textConst, getGuideText, getChronicleTypeComment } from "../../../text";

import { SeedViewer } from "../../../components/SeedViewer";

import  "../../../assets/styles/state-write.css";

const WriteChurch = ({onWriteComplete, shouldRender, useEnforcedWord, isSaving=false}) => {
  const [ writingStage, setWritingStage ] = useState( 0 );
  const [ storyContent, setStoryContent ] = useState('');
  const [ enforcedWord, setEnforcedWord ] = useState( useEnforcedWord );
  const [ storyValidation, setStoryValidation ] = useState({
    word: false,
    isMainValid: false,
    mainSubmitAttempt: false,
  });

  const validateStory = () => {
    const word = storyContent.toLowerCase().indexOf( enforcedWord.toLowerCase() ) > -1;
    const isLongEnough = storyContent.length > writeCfg.all.minLength;
    const validation = {
      ...storyValidation,
      word,
      isMainValid: !!word,
      isLongEnough: isLongEnough,
      mainSubmitAttempt: true
    }

    if( ! validation.isLongEnough ){
      setGuideState({
        ...guideState,
        statement: getGuideText( 'Church', textConst.WRITETOOSHORT )
      })
      return false;
    } 

    if( !validation.isMainValid ){
      setGuideState({
        ...guideState,
        statement: getGuideText( 'Church', textConst.WRITEVALIDATION, [{find: '<mandatoryword>', replace: enforcedWord }] )
      })
    }

    setStoryValidation( validation );
    return validation;
  }

  const formatFinalStory = () => {
    return storyContent;
  }

  const {guideState, setGuideState} = useGuide();
  useEffect(() => {
    if( shouldRender ){
      setGuideState({
        ...guideState,
        statement: getGuideText( 'Church', textConst.WRITEINSTRUCTION, [{find: '<mandatoryword>', replace: enforcedWord }] )
      })
    }
  },[ shouldRender, enforcedWord ]);

  return <>
    <div className="story-area story-area__church">
      <textarea 
        className="write-input-box"
        value={storyContent}
        onChange={ (e) => { setStoryContent(e.target.value) } }
        readOnly={writingStage !== 0}
        placeholder="Write here..."
        spellCheck="false"
      ></textarea>
      <br></br>
      { ( writingStage < 2 )
          ? <MainButton 
            name="Submit story" 
            className="story-submit-button"
            onClick={() => {
              const validation = ( writingStage === 0 ) ? validateStory() : validateAbstract();
              if( writingStage === 0 && validation.isMainValid ){
                onWriteComplete( formatFinalStory() );
                setWritingStage( 1 );
              }
            }}
            pending={isSaving}
          />
          : ''
      }
      <br></br>
      <br></br>
      {(storyValidation.mainSubmitAttempt && !storyValidation.isMainValid)
        ? `You must use ${ enforcedWord } in your story`
        : ''
      }
    </div>
  </>
}

const WriteRadical = ({onWriteComplete, shouldRender, isSaving=false}) => {
  const [ storyContent, setStoryContent ] = useState('');
  const [ isStoryValid, setIsStoryValid ] = useState(false);
  const {guideState, setGuideState} = useGuide();

  const validateStory = () => {
    const isValid = storyContent.length > writeCfg.all.minLength;
    if( ! isValid ){
      setGuideState({
        ...guideState,
        statement: getGuideText( 'Radical', textConst.WRITETOOSHORT )
      });
    }
    setIsStoryValid( isValid );
    return isValid;
  } 

  return <>
    <div className="story-area story-area__radical">
      <textarea
        className="write-input-box"
        value={storyContent} 
        onChange={ (e) => { setStoryContent(e.target.value) } }
        placeholder="Write here..."
        spellCheck="false"
      ></textarea>
      <MainButton 
        className="story-submit-button story-submit-button__radical"
        name="Submit Story"
        onClick={() => {
          const validation = validateStory();
          if( validation ){
            onWriteComplete( storyContent );
          }
        }}
        pending={isSaving}
      />
    </div>
  </> 
}

const WriteWolf = ({onWriteComplete, shouldRender, isSaving=false}) => {
  const [ storyContent, setStoryContent ] = useState('');
  const [ isStoryValid, setIsStoryValid ] = useState(false);
  const textAreaRef = useRef();

  const shuffleStoryChunk = ( story ) => {
    const characters = story.split('');
    const chunkLength = random( Math.min(5, characters.length), Math.min( 15, characters.length ) );
    const chunkStart = random( 0, (characters.length - chunkLength) );
    const chunkEnd = chunkStart + chunkLength;
    const chunk = characters.slice( chunkStart, chunkEnd );    
    const shuffled = shuffle( chunk );
    characters.splice( ...[chunkStart, chunkLength].concat(shuffled) );
    return characters.join('');
  }
  const formatFinalStory = () => {
    return storyContent;
    //return (Math.random() > 0.75) ? shuffleStoryChunk( storyContent ) : storyContent;
  }

  const validateStory = () => {
    const isValid = storyContent.length > writeCfg.all.minLength;
    if( ! isValid ){
      setGuideState({
        ...guideState,
        statement: getGuideText( 'Wolf', textConst.WRITETOOSHORT )
      });
    }
    setIsStoryValid( isValid );
    return isValid;
  }

  const {guideState, setGuideState} = useGuide();

  return <>
    <div className="story-area story-area__wolf">
      <textarea
        className="write-input-box"
        value={storyContent} 
        onChange={ (e) => { setStoryContent(e.target.value) } }
        onKeyDown={ (e) => { 
          if(e.which == 8 || e.which == 46){
             e.preventDefault();
             setGuideState({
              ...guideState,
              statement: 'No deleting! Better be quick..'
            })
          }
        }}
        ref={textAreaRef}
        onSelect={ (e) => { textAreaRef.current.selectionStart = textAreaRef.current.selectionEnd }}
        placeholder="Write here..."
        spellCheck="false"
      ></textarea>    
      <MainButton 
        className="story-submit-button"
        name="Submit Story" 
        onClick={() => {   
          const validation = validateStory();
          if( validation ){
            const final = formatFinalStory();
            setStoryContent( final );
            onWriteComplete( final );
          }
        }}
        pending={isSaving}
      />
    </div>
  </>
}

const WriteInterface = ({
  seedId,
  shouldRender,
  onWriteComplete=()=>{},
  onWriteFail=()=>{},
  enforcedWord
}) => {
  const { address: userAddress, updatePlayer, addSessionStory } = useSessionPlayer();

  const {guideState, setGuideState} = useGuide();

  const [writeComplete, setWriteComplete] = useState(false);
  const [isAddingStory, setIsAddingStory] = useState(false);

  const player = (userAddress) ? useRecord({
    stash,
    table: mudConfig.tables.Player,
    key: { counterValue: userAddress }
  }) : false;

  const username = (player) ? player.name : false;

  const playerFaction = (player) ? useRecord({
    stash,
    table: mudConfig.tables.Fraction,
    key: { counterValue: player.fraction }
  })?.fractionName : false;

  const addStory = async (storyContent: string) => {
    setIsAddingStory(true);
    // Build a StoryPart record so it appears in StateRead during this session
    const hash = `0x${Date.now().toString(16).padStart(40, '0')}${Math.random().toString(16).slice(2).padStart(24, '0')}`;
    addSessionStory({
      counterValue: hash,
      parent: `0x${'0'.repeat(64)}`,
      orgSeed: seedId,
      author: userAddress,
      created: Math.floor(Date.now() / 1000),
      faction: player ? player.fraction : "",
      id: 1,
      posVotes: 0,
      negVotes: 0,
      data: storyContent,
    });
    updatePlayer({ hasWritten: true, currentSeedNumbers: [] });
    setIsAddingStory(false);
    return 'success';
  };

  useEffect( () => {
    if( writeComplete ){
      if( shouldRender ){
        setGuideState({
          ...guideState,
          statement: getGuideText( playerFaction, textConst.WRITEPUBLISH, [{find: '<username>', replace: username }] )
        })
      }
      const handle = setTimeout( () => { 
        if( !shouldRender ) return;
        setWriteComplete( false );
        onWriteComplete();
      }, 5000)
      return () => {
        clearTimeout(handle);
      }
    }
  }, [writeComplete])

  useEffect( () => {
    if( !writeComplete && shouldRender ){
      let handle;   
      const handleDelay = setTimeout( () => {
        handle = setInterval( () => { 
          if( !shouldRender ) return;
          setGuideState({
            ...guideState,
            statement: getGuideText( playerFaction, textConst.CREATIVEPROMPTSWRITING, [{find: '<username>', replace: username }] )
          })
        }, 60000);
      }, 30000 );
      
      return () => {
        clearInterval(handle);
        clearTimeout( handleDelay );
      }
    }
  }, [writeComplete, shouldRender])

  return <>
    <div className="game-state game-state__write">
      <div className="write-interface">
        <SeedViewer seedId={seedId}/>
        {(playerFaction === 'Church' )
          ? <WriteChurch 
              onWriteComplete={ async ( storyContent ) => {
                const addSuccess = await addStory( storyContent );
                if( addSuccess === 'success' ){
                  onWriteComplete();
                } else if( addSuccess === 'funds' ){
                  onWriteFail();
                }                
              }} 
              shouldRender={shouldRender}
              useEnforcedWord={enforcedWord}
              isSaving={isAddingStory}
            />
          : ''
        }
        {(playerFaction === 'Radical') 
          ? <WriteRadical 
              onWriteComplete={ async ( storyContent ) => {
                const addSuccess = await addStory( storyContent );
                if( addSuccess === 'success' ){
                  onWriteComplete();
                } else if( addSuccess === 'funds' ){
                  onWriteFail();
                }   
              }} 
              shouldRender={shouldRender}
              isSaving={isAddingStory}
            />
          : ''
        }
        {(playerFaction === 'Wolf')
          ? <WriteWolf 
              onWriteComplete={ async ( storyContent ) => {
                const addSuccess = await addStory( storyContent );
                if( addSuccess === 'success' ){
                  onWriteComplete();
                } else if( addSuccess === 'funds' ){
                  onWriteFail();
                }   
              }}
              shouldRender={shouldRender}
              isSaving={isAddingStory}
            />
          : '' 
        }
      </div>
    </div>
  </>
}

export const StateWrite = ({
  shouldRender,
  onWriteComplete = () => {},
  onWriteFail = () => {}
}) => {
  const { address: userAddress } = useSessionPlayer();
  const player = (userAddress) ? useRecord({
    stash,
    table: mudConfig.tables.Player,
    key: { counterValue: userAddress }
  }) : false;

  const playerFaction = (player) ? useRecord({
    stash,
    table: mudConfig.tables.Fraction,
    key: { counterValue: player.fraction }
  })?.fractionName : false;

  /* TODO: use the actual faction words saved on chain */
  const factionWords = writeCfg.church.enforcedWords;

  const [enforcedWord, setEnforcedWord] = useState( shuffle( factionWords )[0] );

  const {guideState, setGuideState} = useGuide();
  useEffect(() => {
    if( shouldRender ){
      setGuideState({
        ...guideState,
        statement: (!player.currentSeedNumbers[0])
          ? getGuideText( playerFaction, textConst.WRITEGOROLL )
          : [
              getChronicleTypeComment( player.currentSeedNumbers[0] ),
              getGuideText( playerFaction, textConst.WRITEINSTRUCTION, [{find: '<mandatoryword>', replace: enforcedWord}] )
            ].join('\n\n')
      })
    }
  },[ shouldRender, playerFaction, player ]);

  useEffect(() => {
    setEnforcedWord( shuffle( factionWords )[0] );
  }, [player.currentSeedNumbers[0]] )

  return <>
    <div
      className="game-state game-state__write"
      style={{
        display: (shouldRender) ? 'block' : 'none'
      }}
    >
      {(player.currentSeedNumbers[0])
        ? <WriteInterface
          onWriteComplete={ () => {
            ExtraAudio.sfxSample('pin');
            onWriteComplete();
          }}
          onWriteFail={onWriteFail}
          seedId={player.currentSeedNumbers[0]}
          shouldRender={shouldRender}
          enforcedWord={enforcedWord}
        />
        : ''
      }
    </div>
  </>
}